/**
 * When the server says it is busy, ask again (#392, spec "The pool and the
 * honest overload"). Pure rules; `client.ts` applies them to `fetch`.
 *
 * WHAT "BUSY" IS. An exhausted database pool answers
 *
 *     503 Service Unavailable
 *     Retry-After: 2
 *     {"detail": "Busy, try again shortly."}
 *
 * (`api/overload.py`, #391). A client keys on the status plus the header and
 * never on the words. The header is what separates it from the other 503s: the
 * health probe's `{"status":"unavailable"}` ("this process cannot serve") and
 * sign-in's "Sign in with Apple is not configured" carry none, and are not
 * "ask again in a moment".
 *
 * WHICH REQUESTS ARE SENT AGAIN: reads. A 503 can be raised at ANY connection
 * checkout of a request, not only the first: a handler's session checks out
 * again after each commit, `POST /api/live/start` commits five times and
 * `POST /api/live/{id}/finish` three, so a 503 at the second transaction
 * arrives after the first one wrote, and the client cannot tell (the answer is
 * byte-identical wherever it was raised). A write sent again can be done twice,
 * and a duplicated set, invite or live workout is worse than a visible
 * "busy". So a write that gets a 503 fails once, with `busy: true` on its error.
 *
 * NEVER SENT AGAIN, whatever it carries: 429 (the coach limiter's, and the AI
 * budget's `ai_budget_spent`, whose Retry-After can be days), 401, 403, every
 * other 5xx, a 503 without the header or asking for more than
 * `maxRetryAfterS`, a network error, and the coach chat (a POST).
 *
 * THE TRADE-OFF, named. Retrying buys a smooth busy moment: the user never sees
 * a request the second try would have served. It spends load on a server that is
 * already busy (a burst can multiply by up to 1 + `maxRetries` for reads; the
 * jitter keeps the retries apart and the cap on waiting bounds them) and time
 * before an honest error (about 13 s from the first send against the pool's own
 * 2 s wait, instead of one).
 *
 * EVERY NUMBER IS *(proposed)* until the load test (#396) has run.
 */

import type { ApiError } from './client'

export const BUSY_RETRY = {
  /** Retries after the first send, so up to four requests. *(proposed)* */
  maxRetries: 3,
  /**
   * A Retry-After above this is not a busy moment. *(proposed)* The pool's is 2;
   * a maintenance page or a rate limiter asks for minutes.
   */
  maxRetryAfterS: 10,
  /** The most a request spends waiting between tries, in total. *(proposed)* */
  maxTotalWaitS: 12,
  /** Up to this much more than the wait asked for, drawn at random. *(proposed)* */
  jitterFraction: 0.25,
  /** The wait is at least this, so `Retry-After: 0` cannot be hammered. *(proposed)* */
  minWaitS: 1,
  /**
   * How many busy answers one request has had when it counts as "repeated"
   * failure, the point at which a consumer of `busy.ts` may show something.
   * *(proposed)*
   */
  repeatedAfter: 2,
  /**
   * A streak of busy answers (`busy.ts`) lapses this long after the last one, so
   * a Try again tapped later starts from silence. Longer than one request's whole
   * retry cycle (about 13 s), so a page's second wave of requests, begun when its
   * gate gives up, still finds the streak. *(proposed)*
   */
  streakTtlS: 20,
} as const

/**
 * `Retry-After` as whole seconds from now, or null when it is not one of the
 * two forms RFC 9110 allows: delta-seconds, or an HTTP date. A date in the past
 * is 0. Only the IMF-fixdate form of the date is read; the obsolete ones are not
 * sent by anything here, and a loose date parser would read `2.5` as a day.
 */
export function parseRetryAfterSeconds(value: string | null, nowMs: number = Date.now()): number | null {
  if (value === null) return null
  const text = value.trim()
  if (/^\d+$/.test(text)) return Number(text)
  if (/^[A-Za-z]{3}, \d{2} [A-Za-z]{3} \d{4} \d{2}:\d{2}:\d{2} GMT$/.test(text)) {
    const at = Date.parse(text)
    if (Number.isNaN(at)) return null
    return Math.max(0, Math.ceil((at - nowMs) / 1000))
  }
  return null
}

/**
 * The seconds a busy answer asks us to wait, or null when this answer is not
 * "busy": anything but a 503, a 503 with no usable Retry-After, or one that asks
 * for more than `maxRetryAfterS`.
 */
export function busyRetryAfter(
  status: number,
  retryAfterHeader: string | null,
  nowMs?: number,
): number | null {
  if (status !== 503) return null
  const seconds = parseRetryAfterSeconds(retryAfterHeader, nowMs)
  if (seconds === null || seconds > BUSY_RETRY.maxRetryAfterS) return null
  return seconds
}

/** Reads are sent again; every other method is not (see the head of this file). */
export function isRetryableMethod(method: string | undefined): boolean {
  const upper = (method ?? 'GET').toUpperCase()
  return upper === 'GET' || upper === 'HEAD'
}

/**
 * The wait before retry number `retryNumber` (1, 2, 3), in milliseconds:
 * Retry-After times the retry number, plus up to `jitterFraction` more. The
 * server's own number is the first step (it knows what it is waiting for), the
 * growth keeps a client from asking at the pace the server is struggling at, and
 * the jitter keeps the clients that were refused together from coming back
 * together (Retry-After is a constant, so they would).
 */
export function retryDelayMs(
  retryNumber: number,
  retryAfterS: number,
  random: () => number = Math.random,
): number {
  const base = Math.max(BUSY_RETRY.minWaitS, retryAfterS) * retryNumber
  return Math.round(base * 1000 * (1 + BUSY_RETRY.jitterFraction * random()))
}

/** One request's spend of the retry budget. */
export class BusyBackoff {
  private retries = 0
  private waitedMs = 0

  /**
   * The milliseconds to wait before the next try, which it counts as taken, or
   * null when the budget is spent: `maxRetries` retries, or `maxTotalWaitS` of
   * waiting, whichever comes first.
   */
  next(retryAfterS: number, random: () => number = Math.random): number | null {
    if (this.retries >= BUSY_RETRY.maxRetries) return null
    const delay = retryDelayMs(this.retries + 1, retryAfterS, random)
    if (this.waitedMs + delay > BUSY_RETRY.maxTotalWaitS * 1000) return null
    this.retries += 1
    this.waitedMs += delay
    return delay
  }
}

/** An `ApiError` whose request was refused as busy and is out of retries. */
export function isBusyError(error: unknown): error is ApiError & { busy: true } {
  return typeof error === 'object' && error !== null && (error as ApiError).busy === true
}

/**
 * TanStack Query's `retry` option for every query (`main.tsx`): one more try, as
 * it was, except for a request that already waited through the busy retries.
 * Left to the default, the two layers multiply: up to four requests by
 * `apiFetch`, each tried again by the query, and the user waits twice as long
 * for the same answer. A query that sets `retry` itself keeps its own.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  return failureCount < 1 && !isBusyError(error)
}

/**
 * A wait that an abort ends: the page that asked has gone, or the caller gave up.
 * Rejects the way `fetch` does, so a caller handles one kind of abort.
 */
export function sleepAbortable(ms: number, signal?: AbortSignal | null): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('The operation was aborted.', 'AbortError'))
      return
    }
    const onAbort = () => {
      clearTimeout(timer)
      reject(new DOMException('The operation was aborted.', 'AbortError'))
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}
